import { createRouter, createWebHistory } from 'vue-router';
import type {
  NavigationGuardNext,
  RouteLocationNormalized,
  RouterScrollBehavior,
} from 'vue-router';
import Home from '../views/Home.vue';
import Login from '../views/Login.vue';
import ErrorPageNotFound from '../views/ErrorPageNotFound.vue';
import Help from '../views/Help.vue';
import Dashboard from '../views/Dashboard.vue';
import Project from '../views/Project.vue';
import Setting from '../views/Setting.vue';
import Branding from '../views/Branding.vue';
import Profile from '../views/Profile.vue';
import DeleteAccount from '../views/DeleteAccount.vue';
import ResetPassword from '../views/ResetPassword.vue';
import AccountCreated from '@/views/AccountCreated.vue';
import AddProject from '@/views/AddProject.vue';
import Billing from '../views/Billing.vue';
import AcceptInvitation from '@/views/AcceptInvitation.vue';
import CognitoAuthCallback from '@/views/CognitoAuthCallback.vue';
import { AppContext } from '@/models/AppContext';

/* imported routes and guards from modules */
import commissioningRoutes from '@/modules/commissioning/router/routes';
import {
  comAuthGuard,
  comAccessGuard,
  commissioningProgress,
  serialNumberGuard,
  addressGuard,
} from '@/modules/commissioning/router/guards';
import { cognitoClient } from '@/shared/auth/cognitoClient';
import { isCognitoEnabled, isNativeCognitoEnabled } from '@/shared/auth/cognitoFeatureFlag';
import { isPublicCommissioningEntry } from '@/modules/commissioning/router/utils';
import { useAuthStore } from '@/shared/stores/authStore';
import { isLoggedIn } from '@/shared/auth/session';

const routes = [
  {
    path: '/',
    component: Home,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Home',
        component: Dashboard,
      },
      {
        path: 'add-project',
        component: AddProject,
      },
      {
        path: 'help',
        component: Help,
      },
      {
        path: 'project/:id/:tab',
        component: Project,
      },
      {
        path: 'accept-invitation/:token',
        component: AcceptInvitation,
      },
      {
        path: 'setting',
        component: Setting,
        children: [
          {
            path: 'branding',
            component: Branding,
          },
          {
            path: 'profile',
            name: 'Profile',
            component: Profile,
          },
          {
            path: 'billing',
            component: Billing,
          },
        ],
      },
    ],
  },
  {
    path: '/login',
    component: Login,
  },
  {
    path: '/account-created',
    component: AccountCreated,
  },
  {
    path: '/delete-account/:token',
    component: DeleteAccount,
  },
  {
    path: '/reset-password/:token',
    component: ResetPassword,
  },
  {
    path: '/auth/callback',
    name: 'CognitoAuthCallback',
    component: CognitoAuthCallback,
  },
  {
    path: '/auth/error',
    name: 'AuthError',
    component: () => import('@/views/AuthErrorPage.vue'),
    beforeEnter: (
      _to: RouteLocationNormalized,
      _from: RouteLocationNormalized,
      next: NavigationGuardNext,
    ) => {
      if (isCognitoEnabled) {
        next();
      } else {
        next({ path: '/login' });
      }
    },
  },
  {
    path: '/logout',
    name: 'Logout',
    component: () => import('@/shared/views/Logout.vue'),
    beforeEnter: (
      _to: RouteLocationNormalized,
      _from: RouteLocationNormalized,
      next: NavigationGuardNext,
    ) => {
      if (isCognitoEnabled) {
        next();
      } else {
        next({ path: '/login' });
      }
    },
  },
  {
    path: '/:pathMatch(.*)*',
    component: ErrorPageNotFound,
  },
  ...commissioningRoutes,
];

const scrollBehavior: RouterScrollBehavior = (_to, _from, savedPosition) => {
  if (savedPosition) {
    return savedPosition;
  } else {
    return { top: 0, left: 0, behavior: 'smooth' };
  }
};

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior,
});

//register module guards
router.beforeEach(comAccessGuard);
router.beforeEach(comAuthGuard);
router.beforeEach(commissioningProgress);
router.beforeEach(serialNumberGuard);
router.beforeEach(addressGuard);

router.beforeEach(async (to, from, next) => {
  if (isCognitoEnabled) {
    const needsAuth = to.matched.some((record) => record.meta.requiresAuth);
    const publicComEntry = isPublicCommissioningEntry(to.name);
    const needsComAuth =
      !publicComEntry && to.matched.some((record) => record.meta.requiresComAuth);
    const isLoggedInCognito = await isLoggedIn();

    if ((needsAuth || needsComAuth) && !isLoggedInCognito) {
      if (isNativeCognitoEnabled && needsAuth) {
        next({ path: '/login', query: { returnUrl: to.fullPath } });
        return;
      }
      try {
        await cognitoClient.login(to.fullPath);
      } catch (error) {
        console.error('Cognito sign-in redirect failed:', error);
        next({ name: 'AuthError', query: { retry: 'false' } });
        return;
      }
      return false;
    }
    if (
      isNativeCognitoEnabled &&
      (needsAuth || needsComAuth) &&
      !useAuthStore().isProfileHydrated
    ) {
      next({ path: '/login', query: { returnUrl: to.fullPath } });
      return;
    }
    next();
  } else {
    if (
      to.matched.some((record) => record.meta.requiresAuth) &&
      !(await isLoggedIn(AppContext.SolCentre))
    ) {
      localStorage.setItem('redirectPath', to.path);
      next({ path: '/login' });
    } else {
      next();
    }
  }
});

export default router;
